import { useCallback } from 'react'
import { useMatch } from 'react-router'

import { useQueryClient } from '@fv/client-core'
import type { ListShipment } from '@fv/client-types'
import type { LiveEvent } from '@fv/models'
import type { DTO } from '@fv/models/core'

import { quoteKeys } from '@/hooks/shipments/useQuotes'
import { routes } from '@/routes'

import { loadUploadKeys } from './queries'
import type { UploadSliderRouteState } from './types'

export const useShipmentUploadLiveEventsHandler = () => {
  const queryClient = useQueryClient()
  const quoteQueryTimeoutMap: Record<string, NodeJS.Timeout> = {}
  // biome-ignore lint/correctness/useExhaustiveDependencies: we need to use the timeout map here
  return useCallback(
    (event: DTO<LiveEvent>) => {
      if (event.eventType === 'shipper:shipment:add') {
        const uploadId = event.data.shipment.upload?._id
        if (!uploadId) return

        queryClient.setQueryData<ListShipment[]>(
          loadUploadKeys.byUpload(uploadId),
          prev => {
            if (!prev) return [event.data.shipment]
            if (prev.find(s => s.loadId === event.data.shipment.loadId))
              return prev
            return [...prev, event.data.shipment]
          },
        )
      }

      if (event.eventType === 'shipper:shipment:update') {
        const uploadId = event.data.uploadId
        if (!uploadId) return

        queryClient.setQueryData<ListShipment[]>(
          loadUploadKeys.byUpload(uploadId),
          prev => {
            if (!prev) return prev
            return prev.map(s =>
              s.loadId === event.data.loadId
                ? {
                    ...s,
                    ...event.data.fields,
                  }
                : s,
            )
          },
        )

        if (
          event.data.fields.quoteProcessStatus &&
          event.data.fields.quoteProcessStatus === 'ok'
        ) {
          const quoteTimeout = quoteQueryTimeoutMap[event.data.loadId]
          if (quoteTimeout) {
            clearTimeout(quoteTimeout)
          }
          quoteQueryTimeoutMap[event.data.loadId] = setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: quoteKeys.load(event.data.loadId),
            })
          }, 2000)
        }
      }
    },
    [queryClient],
  )
}

export function useUploadView(): UploadSliderRouteState {
  const newMatch = useMatch(routes.uploadNew())
  const manageMatch = useMatch({ path: routes.uploads(), end: true })
  const detailMatch = useMatch(routes.uploadDetail(':uploadId'))

  if (newMatch) {
    return { isOpen: true, mode: 'new' }
  }

  if (manageMatch) {
    return { isOpen: true, mode: 'manage' }
  }

  if (detailMatch?.params.uploadId) {
    return {
      isOpen: true,
      mode: 'view',
      uploadId: detailMatch.params.uploadId,
    }
  }

  return { isOpen: false, mode: 'manage' }
}
